import type React from "react";
import { useContext, useEffect, useRef, useState } from "react";
import { Dialog, Popover } from "@material-ui/core";
import AccountIcon from "../atoms/icons/AccountIcon";
import AvatarSelectDialog from "./AvatarSelectDialog";
import { postImage } from "../../utils/api/postImage";
import { getSubdomain } from "../../utils/getSubdomain";
import type TApiResult from "../../types/api/TApiResult";
import { postMemberAccessLog } from "../../utils/api/postMemberAccessLog";
import { ACCESS_LOG_ACTION } from "../../types/TAppAccessLogAction";
import { getCommonMemberIcon } from "../../utils/api/getICommonMemberIcon";
import {
  fetchNGWords,
  type TApiNGWordsResult,
} from "../../utils/api/fetchNGWords";
import {
  postMemberIconNicknameUpdate,
  type TApiMemberIconNicknameUpdateValidateErrors,
} from "../../utils/api/postMemberIconNicknameUpdate";
import { LoginUserContext } from "../../providers/LoginUserProvider";
import STEditAvatarNickname from "../../assets/styles/organisms/editAvatarNickname.module.scss";
import STButton from "../../assets/styles/atoms/button.module.scss";
import STForm from "../../assets/styles/atoms/form/reactHookForms/form.module.scss";
import { BoardDetailContext } from "../../providers/BoardDetailProvider";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

type AvatarTypeKey = "upload" | "icon" | "none";
type EditNicknameAvatarProps = {
  nickname: string;
  avatar_url: string;
  onClose: () => void;
  onSuccess?: (params: {
    nickname: string;
    avatar_url: string;
    name: string;
    avatarType: AvatarTypeKey;
  }) => void;
};

const EditNicknameAvatar: React.FC<EditNicknameAvatarProps> = ({
  nickname,
  avatar_url,
  onClose,
  onSuccess,
}) => {
  const [newNickname, setNewNickname] = useState(nickname || "");
  type AvatarType = {
    presign_url: string;
    file_url: string;
  };

  const [selectedAvatar, setSelectedAvatar] = useState<string | AvatarType>(
    avatar_url
  );
  const [openAvatarDialog, setOpenAvatarDialog] = useState(false);
  const [selectedAvatarFile, setSelectedAvatarFile] = useState<File | null>(
    null
  );
  const [lastConfirmedAvatar, setLastConfirmedAvatar] = useState<
    string | AvatarType
  >(avatar_url);
  const [lastConfirmedOption, setLastConfirmedOption] = useState<string>("0");

  const [selectedOption, setSelectedOption] = useState("0");
  type CommonMemberIcon = {
    file_url: string;
    presign_url?: string;
  };
  const [commonMemberIcons, setCommonMemberIcons] = useState<
    CommonMemberIcon[]
  >([]);
  type NgWord = {
    word: string;
  };
  const [ngWords, setNgWords] = useState<NgWord[]>([]);
  const [hasNgword, setHasNgwords] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [uploadAvatarErrorMessage, setUploadAvatarErrorMessage] = useState("");
  const uploadRef = useRef<HTMLInputElement | null>(null);
  const subdomain = getSubdomain(window.location.hostname);
  const { setMember, member } = useContext(LoginUserContext);
  const boardContext = useContext(BoardDetailContext);
  const [actionAnchorEl, setActionAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const handleClickActionButton = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setActionAnchorEl(event.currentTarget);
  };
  const handleCloseActionButton = () => {
    setActionAnchorEl(null);
  };
  const openActionPopover = Boolean(actionAnchorEl);
  const actionId = openActionPopover ? "action-popover" : undefined;
  const actions = [
    {
      title: "画像をアップロード",
      value: "1",
    },
    {
      title: "アイコンから選択",
      value: "2",
    },
  ];

  const countChars = (input: string) => {
    if (!input) return 0;
    const SINGLE_BYTE = /[\u0020-\u1fff\uff61-\uff9f]/g;
    const DOUBLE_BYTE = /[\u2000-\uff60\uffa0-\uffff]/g;

    const singleByteCount = (input.match(SINGLE_BYTE) || []).length;
    const doubleByteCount = (input.match(DOUBLE_BYTE) || []).length * 2;
    return singleByteCount + doubleByteCount;
  };

  useEffect(() => {
    const fetchData = async () => {
      const response = await getCommonMemberIcon();
      setCommonMemberIcons(response.data);
    };

    const fetchNgwordData = async () => {
      const res: TApiResult & TApiNGWordsResult = await fetchNGWords();
      setNgWords(Array.isArray(res.result) ? res.result : []);
    };

    fetchData();
    fetchNgwordData();
  }, []);

  useEffect(() => {
    setNewNickname(nickname || "");
    setSelectedAvatar(avatar_url);
    setSelectedOption("0");
    setSelectedAvatarFile(null);
    setHasNgwords(false);
    setErrorMessage("");
    setUploadAvatarErrorMessage("");
  }, [nickname, avatar_url]);

  const handleNicknameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let error = "";
    const value = e.target.value;
    const isHasNgWord =
      ngWords.filter(
        (item) =>
          item.word && value.toLowerCase().includes(item.word.toLowerCase())
      ).length > 0;
    setHasNgwords(isHasNgWord);
    if (isHasNgWord) {
      error = "NGワードが含まれているため設定ができません。";
    } else if (countChars(value) > 40) {
      error = "20文字以内で入力してください";
    }

    setErrorMessage(error);
    setNewNickname(value);
  };

  const handleOptionChange = (value: string) => {
    setSelectedOption(value);
    if (value === "1") {
      uploadRef.current?.click();
    } else {
      setOpenAvatarDialog(true);
    }
  };

  const onCloseAvatarDialog = (newAvatar: string, isChange: boolean) => {
    if (isChange) {
      setSelectedAvatar(newAvatar);
      setLastConfirmedAvatar(newAvatar);
      setSelectedOption("2");
      setLastConfirmedOption("2");
    } else {
      setSelectedAvatar(lastConfirmedAvatar);
      setSelectedOption(lastConfirmedOption);
    }
    setOpenAvatarDialog(false);
  };

  const handleImageUpload = async (file: File | string) => {
    if (typeof file === "string") return file;
    const formData = new FormData();
    const location = `${subdomain}/images/message-board`;
    formData.append("picture", file);
    formData.append("location", location);
    try {
      const response = await postImage(formData);
      return response?.data;
    } catch (error) {
      throw new Error("画像のアップロードに失敗しました");
    }
  };

  const updateBoardNicknameAvatar = (
    uid: string,
    nickname: string,
    avatar_url: string,
    name: string
  ) => {
    if (!boardContext) return;
    boardContext.updateNewNicknameAvatar(uid, nickname, avatar_url, name);
  };

  const handleSave = async () => {
    let finalAvatarUrl = avatar_url;
    let presign_url = "";

    if (lastConfirmedOption === "1") {
      if (selectedAvatarFile) {
        const upload = await handleImageUpload(selectedAvatarFile);
        finalAvatarUrl = upload?.file_url || "";
        presign_url = upload?.presign_url || "";
      } else if (typeof selectedAvatar === "string") {
        finalAvatarUrl = selectedAvatar;
      }
    } else if (lastConfirmedOption === "2") {
      if (typeof selectedAvatar === "object") {
        finalAvatarUrl = selectedAvatar.file_url;
        presign_url = selectedAvatar.presign_url || "";
      } else {
        finalAvatarUrl = selectedAvatar;
      }
    }
    // import databse
    const params = {
      avatar_url: finalAvatarUrl,
      nickname: newNickname,
      name: member?.name,
    };
    const res = await postMemberIconNicknameUpdate(params);
    if (res.isSuccess) {
      const resolvedUrl = presign_url || finalAvatarUrl;
      onSuccess?.({
        nickname: newNickname,
        avatar_url: resolvedUrl,
        name: member?.name || "",
        avatarType:
          lastConfirmedOption === "1"
            ? "upload"
            : lastConfirmedOption === "2"
            ? "icon"
            : "none",
      });

      if (setMember) {
        setMember((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            nickname: newNickname,
            avatar_url: resolvedUrl,
          };
        });
      }
      if (member?.uid) {
        updateBoardNicknameAvatar(
          member.uid,
          newNickname,
          resolvedUrl,
          member.name
        );
      }
      postMemberAccessLog(ACCESS_LOG_ACTION.memberEdit, document.referrer);
      onClose();
    } else {
      const message = res.message || "";
      if (message) {
        const parseMessageError = JSON.parse(message) ?? {};
        const messageErrorData = parseMessageError?.data ?? {};
        const messageErrorNickname = messageErrorData?.nickname ?? "";
        if (messageErrorNickname) setErrorMessage(messageErrorNickname);
      }
    }
  };

  return (
    <>
      {/* Main Profile Dialog */}
      <Dialog
        classes={{
          root: STEditAvatarNickname.dialog,
        }}
        className={STEditAvatarNickname.dialog}
        open={true}
        onClose={onClose}
        aria-labelledby="profile-dialog-title"
        PaperProps={{
          style: {
            padding: 10,
          },
        }}
      >
        <div className={STEditAvatarNickname.dialog_header}>
          <p className={STEditAvatarNickname.title}>
            ニックネーム・アイコン画像編集
          </p>
        </div>
        <div>
          <p>※変更すると、掲示板全体でニックネームと</p>
          <p>アイコン画像が更新されます。</p>
        </div>
        <div className={STEditAvatarNickname.content_container}>
          <div className={STEditAvatarNickname.avatar_container}>
            <div className={STEditAvatarNickname.avatar_wrapper}>
              <p>アイコン画像</p>
              {selectedAvatar && (
                <img
                  src={
                    selectedOption === "1"
                      ? selectedAvatarFile
                        ? URL.createObjectURL(selectedAvatarFile)
                        : typeof selectedAvatar === "object"
                        ? selectedAvatar.presign_url
                        : typeof selectedAvatar === "string"
                        ? selectedAvatar
                        : avatar_url
                      : selectedOption === "2" &&
                        typeof selectedAvatar === "object"
                      ? selectedAvatar.presign_url
                      : typeof selectedAvatar === "string"
                      ? selectedAvatar
                      : avatar_url
                  }
                  alt="avatar"
                  className={STEditAvatarNickname.avatar_thumbnail}
                />
              )}
              {!selectedAvatar && <AccountIcon width={60} height={60} />}
            </div>
            <div>
              <button
                type="button"
                className={`${STEditAvatarNickname.uploadButton}`}
                onClick={handleClickActionButton}
              >
                アイコン画像を選択
              </button>
              <Popover
                id={actionId}
                open={openActionPopover}
                anchorEl={actionAnchorEl}
                onClose={handleCloseActionButton}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
              >
                <button type="button" onClick={handleCloseActionButton}>
                  {actions.map((action) => {
                    const { title, value } = action;
                    return (
                      <div
                        key={title}
                        className={STEditAvatarNickname.actionContainer}
                      >
                        <button
                          type="button"
                          onClick={() => handleOptionChange(value)}
                        >
                          {title}
                        </button>
                      </div>
                    );
                  })}
                </button>
              </Popover>
            </div>
          </div>
          <div className={STEditAvatarNickname.errorTextWrapper}>
            <div className={STEditAvatarNickname.errorText}>
              {uploadAvatarErrorMessage}
            </div>
          </div>

          {/* Nickname Section */}
          <p>ニックネーム</p>
          <div className={STEditAvatarNickname.nickname_input_container}>
            <input
              type="text"
              value={newNickname}
              onChange={handleNicknameChange}
              className={STForm.form}
            />
          </div>
          <div className={STEditAvatarNickname.errorTextWrapper}>
            <div className={STEditAvatarNickname.errorText}>{errorMessage}</div>
          </div>
        </div>
        <div className={STEditAvatarNickname.dialog_footer_container}>
          <div className={STEditAvatarNickname.buttonGroup}>
            <button
              type="button"
              className={`${STEditAvatarNickname.backButton} ${STButton.secondary}`}
              onClick={onClose}
            >
              キャンセル
            </button>
            <button
              type="button"
              className={`${STEditAvatarNickname.submitButton} ${STButton.secondary}`}
              onClick={handleSave}
              disabled={
                Boolean(errorMessage) || Boolean(uploadAvatarErrorMessage)
              }
            >
              保存
            </button>
          </div>
        </div>
      </Dialog>

      {/* Upload image from device */}
      <input
        accept="image/*"
        type="file"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const selectedFiles: FileList | null = e?.target?.files;
          if (!selectedFiles?.length) {
            setSelectedAvatar(lastConfirmedAvatar);
            setSelectedOption(lastConfirmedOption);
            return;
          }
          const file = selectedFiles[0];
          if (file.size > MAX_FILE_SIZE) {
            setUploadAvatarErrorMessage(
              "ファイルサイズは10MB以下の画像を指定してください。"
            );
          } else {
            setUploadAvatarErrorMessage("");
          }
          const previewUrl = URL.createObjectURL(file);

          setSelectedAvatarFile(file);
          setSelectedAvatar(previewUrl);
          setLastConfirmedAvatar(previewUrl);
          setSelectedOption("1");
          setLastConfirmedOption("1");
        }}
        style={{ display: "none" }}
        ref={uploadRef}
      />

      {/* Avatar Selection Dialog */}
      <AvatarSelectDialog
        open={openAvatarDialog}
        onClose={(newAvatar: string, isChange: boolean) => {
          setUploadAvatarErrorMessage("");
          onCloseAvatarDialog(newAvatar, isChange);
        }}
        icons={commonMemberIcons}
      />
    </>
  );
};

export default EditNicknameAvatar;
